import { zodResolver } from '@hookform/resolvers/zod';
import { Button, FormControlLabel, FormHelperText, Grid, Paper, Stack, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { Control, Controller, useForm } from 'react-hook-form';
import { AFFILIATE_COMMISSION_TYPE_OPTIONS, QUERY_KEYS } from '../../../constants';
import API_ROUTES from '../../../constants/apiRoutes';
import { useToast } from '../../../contexts/ToastContext';
import { useMarketplace } from '../../../hooks/useMarketplace';
import usePermissions from '../../../hooks/usePermissions';
import { useToastErrorMessage } from '../../../hooks/useToastErrorMessage';
import { affiliateCommissionSchema } from '../../../schema';
import { patchData, postData } from '../../../utils/api';
import { mergeSx } from '../../../utils/styles';
import { StyledSwitch } from '../../atoms/Switch';
import FormNumberInput from '../../molecules/FormNumberInput';
import FormSelect from '../../molecules/FormSelect';
import CommissionTabHeader from './CommissionTabHeader';
import {
  AffiliateCommissionResponse,
  AffiliateCommissionFormData,
  AffiliateQualificationField,
  mapAffiliateCommissionPatch,
  mapAffiliateCommissionToForm,
} from './commissionTypes';
import { styles } from './styles';
import useCommissionQueries from './useCommissionQueries';

type QualificationToggle = {
  field: AffiliateQualificationField;
  label: string;
};

const NEW_PLAYER_QUALIFICATION: QualificationToggle = {
  field: 'newPlayer',
  label: 'New Player',
};

const KYC_QUALIFICATION: QualificationToggle = {
  field: 'kyc',
  label: 'KYC',
};

const BASE_QUALIFICATION_TOGGLES = [NEW_PLAYER_QUALIFICATION];
const KYC_QUALIFICATION_TOGGLES = [...BASE_QUALIFICATION_TOGGLES, KYC_QUALIFICATION];

const affiliateStyles = {
  criteria: {
    boxShadow: 0,
    mt: 2,
    p: 2,
  },
  criterion: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    py: 1,
  },
} as const;

type AffiliateCommissionFieldsProps = {
  control: Control<AffiliateCommissionFormData>;
  disabled: boolean;
};

const AffiliateCommissionFields = ({ control, disabled }: AffiliateCommissionFieldsProps) => (
  <Grid container spacing={2} sx={mergeSx(styles.gridContainer, { mt: 1 })}>
    <Grid size={12}>
      <FormSelect
        name='affiliateCommissionType'
        label='Affiliate Commission Type'
        control={control}
        options={AFFILIATE_COMMISSION_TYPE_OPTIONS}
        disabled
        fullWidth
        sx={styles.input}
      />
    </Grid>
    <Grid size={12}>
      <FormNumberInput
        control={control}
        name='cpaAmount'
        label='CPA Amount'
        allowDecimals
        displayCurrency
        valueAsString
        fullWidth
        disabled={disabled}
        sx={styles.inputWithoutPadding}
      />
    </Grid>
    <Grid size={12}>
      <FormNumberInput
        control={control}
        name='holdPeriod'
        label='Hold Period (days)'
        valueAsString
        fullWidth
        disabled={disabled}
        sx={styles.inputWithoutPadding}
      />
    </Grid>
    <Grid size={12}>
      <FormNumberInput
        control={control}
        name='minSportsbookWagering'
        label='Minimum Sportsbook Wagering'
        allowDecimals
        displayCurrency
        valueAsString
        fullWidth
        disabled={disabled}
        sx={styles.inputWithoutPadding}
      />
    </Grid>
    <Grid size={12}>
      <FormNumberInput
        control={control}
        name='minCasinoWagering'
        label='Minimum Casino Wagering'
        allowDecimals
        displayCurrency
        valueAsString
        fullWidth
        disabled={disabled}
        sx={styles.inputWithoutPadding}
      />
    </Grid>
    <Grid size={12}>
      <FormHelperText>
        These values are used for affiliate CPA qualification. Meeting either configured minimum satisfies the wagering
        criterion. Leave blank or enter 0 to disable a requirement.
      </FormHelperText>
    </Grid>
  </Grid>
);

type QualificationCriteriaProps = {
  control: Control<AffiliateCommissionFormData>;
  disabled: boolean;
  toggles: QualificationToggle[];
};

const QualificationCriteria = ({ control, disabled, toggles }: QualificationCriteriaProps) => (
  <Paper sx={affiliateStyles.criteria}>
    <Typography variant='h6'>Qualification criteria</Typography>
    {toggles.map(({ field, label }) => (
      <Stack key={field} sx={affiliateStyles.criterion}>
        <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
        <Controller
          name={field}
          control={control}
          render={({ field: { value, onChange, name } }) => (
            <FormControlLabel
              control={
                <StyledSwitch
                  checked={value}
                  onChange={(_event, checked) => onChange(checked)}
                  disabled={disabled}
                  slotProps={{ input: { 'aria-label': label } }}
                  name={name}
                />
              }
              label=''
            />
          )}
        />
      </Stack>
    ))}
  </Paper>
);

type CommissionFormActionsProps = {
  isSaving: boolean;
  onCancel: () => void;
  onSave: () => void;
};

const CommissionFormActions = ({ isSaving, onCancel, onSave }: CommissionFormActionsProps) => (
  <Stack direction='row' spacing={1} sx={{ mt: 2, justifyContent: 'flex-end' }}>
    <Button variant='outlined' onClick={onCancel} disabled={isSaving}>
      Cancel
    </Button>
    <Button onClick={onSave} disabled={isSaving}>
      Save
    </Button>
  </Stack>
);

const AffiliateCommissionTab = () => {
  const { selectedCRMUserId, userData, commissionData, isCommissionLoading, isCommissionError, isCommissionSuccess } =
    useCommissionQueries<AffiliateCommissionResponse>();

  const { hasPermission } = usePermissions();
  const { isKycEnabled } = useMarketplace();
  const canEditCommission = hasPermission('editPartnerCommission');
  const qualificationToggles = isKycEnabled ? KYC_QUALIFICATION_TOGGLES : BASE_QUALIFICATION_TOGGLES;

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useToast();
  const { handleErrorMessage } = useToastErrorMessage();

  const initialFormData = useMemo(() => mapAffiliateCommissionToForm(commissionData), [commissionData]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<AffiliateCommissionFormData>({
    defaultValues: initialFormData,
    resolver: zodResolver(affiliateCommissionSchema),
  });

  useEffect(() => {
    if (!isDirty) {
      reset(initialFormData);
    }
  }, [initialFormData, isDirty, reset]);

  const saveMutation = useMutation({
    mutationFn: async (data: AffiliateCommissionFormData) => {
      if (!selectedCRMUserId) return;

      const payload = mapAffiliateCommissionPatch(
        data,
        commissionData?.cpa,
        qualificationToggles.map(({ field }) => field)
      );

      if (commissionData) {
        await patchData(`${API_ROUTES.CRM.AGENT_COMMISSION}/${selectedCRMUserId}`, payload, 'crm');
        return;
      }

      await postData(API_ROUTES.CRM.AGENT_COMMISSION, { ...payload, agentId: selectedCRMUserId }, 'crm');
    },
    onSuccess: async (_response, submittedData) => {
      reset(submittedData);
      enqueueSnackbar({ message: 'Commission updated successfully', variant: 'success' });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.crmCommissions, selectedCRMUserId] });
    },
    onError: handleErrorMessage,
  });

  const isSaving = saveMutation.isPending;
  const isFormDisabled =
    !canEditCommission || isSaving || isCommissionLoading || isCommissionError || !isCommissionSuccess;
  const showFormActions = isDirty && canEditCommission && isCommissionSuccess && !isCommissionError;

  return (
    <>
      <CommissionTabHeader user={userData} />

      <AffiliateCommissionFields control={control} disabled={isFormDisabled} />
      <QualificationCriteria control={control} disabled={isFormDisabled} toggles={qualificationToggles} />

      {showFormActions && (
        <CommissionFormActions
          isSaving={isSaving}
          onCancel={() => reset(initialFormData)}
          onSave={handleSubmit((data) => saveMutation.mutate(data))}
        />
      )}
    </>
  );
};

export default AffiliateCommissionTab;
